import {
  Dispatch,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import type {
  MergeCountsRequest,
  MergeDecisionDto,
  MergePeriodDto,
  MergeRowDto,
  MergeRowsFilter,
  MergeRowsRequest,
  MergeSection,
  MergeSort,
} from '../api/mergeResolution.types';
import { MergeApiError, toMergeApiError } from '../api/MergeApiError';
import type {
  MergeResolutionDataSource,
  MergeRouteIds,
} from '../api/MergeResolutionDataSource';
import { PAGE_SIZE } from '../config';
import { periodKey } from '../state/cellKey';
import {
  clearDecision,
  customDecision,
  validateCustomValue,
} from '../state/customValue';
import { applyRebase, sendableDecisions } from '../state/rebaseApply';
import { initialState, resolutionReducer } from '../state/reducer';
import { expandSelection, rowCellKeys } from '../state/selection';
import { detectStructuralBlock } from '../state/structural';
import type {
  CellKey,
  PeriodRangeMode,
  RebaseApplied,
  ResolutionAction,
  ResolutionState,
  SessionIds,
} from '../state/types';

// Only sort the server supports for this screen; no action changes it.
const ROWS_SORT: MergeSort = 'input_row_id';

// CURRENT_VERSION_UNAVAILABLE means the version writer has not finished. One
// retry after this delay, then the error is surfaced.
const CURRENT_VERSION_RETRY_MS = 2000;

// Retry budgets are tracked per operation so a diff retry does not consume
// the budget belonging to rows.
type MergeOp = 'diff' | 'rows';

// Operations whose AbortController has to live in a ref, because the call
// that supersedes one of them is issued from a different closure than the one
// that started it. `diff` is deliberately absent: it runs inside an effect,
// and that effect's cleanup is both its supersession hook and its unmount
// hook, so its controller stays in the effect scope.
type MergeAbortOp = 'rows' | 'rebase';

// An abort is what supersession and unmount look like at the transport, so it
// is expected rather than a failure: it must never reach `error`, never flip
// a loading flag and never dispatch. Mapping covers both the real adapter's
// DOMException and an 'ABORTED' MergeApiError.
const isAbort = (err: unknown): boolean =>
  toMergeApiError(err).code === 'ABORTED';

export interface MergeSessionActions {
  // Every row-level action names the section it acts on. The screen renders
  // one grid per section, so the section a control belongs to is not
  // necessarily `ui.section`; resolving rows from the active section instead
  // would act on the other section's page and apply the wrong section rule.
  takeSelection(
    rowIds: string[],
    section: MergeSection,
    choice: 'MINE' | 'THEIRS'
  ): void;
  takeRow(
    rowId: string,
    section: MergeSection,
    choice: 'MINE' | 'THEIRS'
  ): void;
  // Upserts decisions the caller has already built (the cell-range toolbar's
  // `expandCellRange` output). Like every decision action it only updates the
  // local state: the counts follow from it, and the server is next asked at a
  // section/input/page change and at Submit.
  applyDecisions(decisions: MergeDecisionDto[]): void;
  undoRow(rowId: string, section: MergeSection): void;
  custom(
    row: MergeRowDto,
    period: MergePeriodDto,
    raw: string
  ): { ok: true } | { ok: false; reason: string };
  clear(row: MergeRowDto, period: MergePeriodDto): boolean;
  // Drops the decision on one cell, so it goes back to its auto value. For an
  // override on a cell that is not a conflict, where there is no decision to
  // fall back to and Clear would blank it.
  revert(row: MergeRowDto, period: MergePeriodDto): void;
  selectSection(section: MergeSection): void;
  selectInput(inputKey: string | null): void;
  expand(rowId: string | null): void;
  toggleChangedUnderneath(value: boolean): void;
  setPeriodRange(mode: PeriodRangeMode): void;
  // Asks merge/rows for this 1-based page of the section; the rows effect refetches.
  setPage(section: MergeSection, page: number): void;
  // Shared and single-flight. `useSubmitFlow` awaits this instead of calling
  // `ds.rebase` itself, so only one rebase is ever in flight for a session;
  // a caller that arrives while one is running joins it and settles with the
  // same result, or the same error.
  rebase(): Promise<RebaseApplied>;
  retry(): void;
}

export interface MergeSessionResult {
  state: ResolutionState;
  dispatch: Dispatch<ResolutionAction>;
  actions: MergeSessionActions;
  loading: { diff: boolean; rows: boolean };
  // True from the moment a rebase starts until the shared promise settles,
  // whether it resolved or rejected.
  rebasing: boolean;
  newConflicts: number | null;
  error?: MergeApiError;
}

// After a rebase, rows name the version the decisions moved from,
// which makes the server recompute the REOPENED / AUTO_REBASED markers, plus
// the rebase's carrier when it is non-empty. The API accepts `Reopened` only
// together with `PreviousCurrentSubmissionID`, so neither is sent before a
// rebase.
function rebaseMarkers(
  s: ResolutionState
): Pick<MergeCountsRequest, 'PreviousCurrentSubmissionID' | 'Reopened'> {
  const previous = s.session.previousCurrentSubmissionId;
  if (previous === undefined) {
    return {};
  }
  return s.reopenedCarrier.length > 0
    ? { PreviousCurrentSubmissionID: previous, Reopened: s.reopenedCarrier }
    : { PreviousCurrentSubmissionID: previous };
}

// What a loaded page was loaded for. A section's page is reused when the user
// comes back to it, but only while nothing that shapes its rows has moved:
// the filters, its page number, the current version, or the decisions and
// rebase markers the rows request carries. Those are compared by reference,
// so any decision, rebase or reopen invalidates both sections.
type LoadedMark = { key: string; refs: unknown[] };

function loadedMark(s: ResolutionState, section: MergeSection): LoadedMark {
  return {
    key: JSON.stringify([
      s.ui.inputKey,
      s.ui.changedUnderneathOnly,
      s.ui.requestedPages[section] ?? 1,
      s.session.currentSubmissionId,
      s.session.previousCurrentSubmissionId,
    ]),
    refs: [
      s.decisions,
      s.noDecisionNeeded,
      s.reopened,
      s.reopenedCarrier,
      s.autoRebased,
    ],
  };
}

function isCurrent(mark: LoadedMark | undefined, now: LoadedMark): boolean {
  return (
    mark !== undefined &&
    mark.key === now.key &&
    mark.refs.every((ref, i) => ref === now.refs[i])
  );
}

// For a cell that cannot take the requested choice. Deliberately carries no
// cell value.
const ILLEGAL_CHOICE_REASON = 'This choice is not available for this cell.';

export function useMergeSession(
  ds: MergeResolutionDataSource,
  ids: MergeRouteIds,
  session: Pick<SessionIds, 'groupRowIdentifierId' | 'submissionId'>
): MergeSessionResult {
  const [state, dispatch] = useReducer(resolutionReducer, undefined, () =>
    initialState({
      groupRowIdentifierId: session.groupRowIdentifierId,
      submissionId: session.submissionId,
      baseSubmissionId: 0,
      currentSubmissionId: 0,
    })
  );

  const [error, setError] = useState<MergeApiError | undefined>(undefined);
  const [newConflicts, setNewConflicts] = useState<number | null>(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [rebasing, setRebasing] = useState(false);
  // Turns true once the diff has landed; gates the rows fetch so the
  // bootstrap order is diff → rows.
  const [booted, setBooted] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // Mirror of the latest state for callbacks that run outside render (the
  // rows effect). Declared first so it is refreshed
  // before any other effect in the same commit.
  const stateRef = useRef(state);
  // Per section: what its loaded page was fetched for (see `loadedMark`).
  const loadedRef = useRef<Partial<Record<MergeSection, LoadedMark>>>({});
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Cancellation and correctness are separate mechanisms, and the second is
  // the load-bearing one. The controllers below only stop wasted work: a
  // response can already be in the microtask queue when the abort fires, so
  // a superseded request may still resolve. Every response is therefore
  // admitted only while mounted and while its sequence number is still the
  // newest for that operation - that pair is what stops a late response
  // overwriting newer state.
  const mountedRef = useRef(true);
  const seqRef = useRef<Record<MergeOp, number>>({
    diff: 0,
    rows: 0,
  });
  // The newest controller for each abortable operation. A slot is written
  // before the controller it displaces is aborted, and is cleared only by the
  // request that owns it (identity-checked), so an abort can neither be
  // observed against a half-written slot nor null out a slot that has already
  // passed to a newer request - which would leave that newer request with
  // nothing for unmount to abort.
  const abortRef = useRef<Record<MergeAbortOp, AbortController | null>>({
    rows: null,
    rebase: null,
  });
  const retriedRef = useRef<Record<MergeOp, boolean>>({
    diff: false,
    rows: false,
  });
  const timersRef = useRef<Set<number>>(new Set());
  // One rebase at a time: two operations can fail with REBASE_REQUIRED at
  // once, a submit rebases before it sends, and two rebase calls would
  // advance the current submission twice. Every caller awaits this one
  // promise, `useSubmitFlow` included (via `actions.rebase`).
  const rebaseRef = useRef<Promise<RebaseApplied> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // mountedRef is already false, so nothing these rejections reach can
      // commit even if one settles before this cleanup returns. The slots are
      // nulled because React re-runs this effect against the same refs under
      // StrictMode, and a remount must not re-abort a dead controller.
      abortRef.current.rows?.abort();
      // The rebase is deliberately NOT aborted. It is a POST that advances
      // the current submission server-side, so aborting the transport does
      // not undo it, and `mountedRef` already stops anything committing after
      // unmount. Aborting it would also reject the in-flight promise during
      // teardown, where the handler's microtask may never run - an unhandled
      // rejection that crashes the Jest worker.
      abortRef.current.rows = null;
      abortRef.current.rebase = null;
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current.clear();
    };
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timersRef.current.delete(id);
      if (mountedRef.current) {
        fn();
      }
    }, ms);
    timersRef.current.add(id);
  }, []);

  const requestBase = useCallback(() => {
    const s = stateRef.current.session;
    return {
      GroupRowIdentifierID: s.groupRowIdentifierId,
      MineSource: 'staging' as const,
      SubmissionID: s.submissionId,
    };
  }, []);

  // The rail's tab key is Input_Name when the server sent ByInput and
  // Tab_Name otherwise (see `inputTabs` in state/labels.ts), so the filter
  // field follows the same test.
  const rowsFilter = useCallback(
    (s: ResolutionState): MergeRowsFilter | undefined => {
      const filter: MergeRowsFilter = {};
      if (s.ui.inputKey !== null) {
        if (s.serverCounts?.ByInput) {
          filter.Input_Name = s.ui.inputKey;
        } else {
          filter.Tab_Name = s.ui.inputKey;
        }
      }
      if (s.ui.changedUnderneathOnly) {
        filter.ChangedUnderneath = true;
      }
      return Object.keys(filter).length > 0 ? filter : undefined;
    },
    []
  );

  const rebase = useCallback((): Promise<RebaseApplied> => {
    if (rebaseRef.current) {
      return rebaseRef.current;
    }
    // Single-flight means a rebase is never superseded, so this slot is
    // provably empty when written. The controller is never aborted by this
    // hook (not even on unmount, see the cleanup effect); it exists only to
    // give `ds.rebase` a signal to pass through.
    const controller = new AbortController();
    abortRef.current.rebase = controller;
    setRebasing(true);
    // A rebase moves the current version: nothing loaded before it is reusable.
    loadedRef.current = {};
    const run = (async (): Promise<RebaseApplied> => {
      const s = stateRef.current;
      try {
        const res = await ds.rebase(
          ids,
          {
            ...requestBase(),
            PreviousCurrentSubmissionID: s.session.currentSubmissionId,
            Decisions: sendableDecisions(s.decisions, s.noDecisionNeeded),
            // The last rebase's echo, unchanged; [] on a first rebase, which
            // opts in to the echo.
            Reopened: s.reopenedCarrier,
          },
          controller.signal
        );
        const applied = applyRebase(res);
        if (!mountedRef.current) {
          // Nothing is committed after unmount, but the caller still gets
          // the result it awaited; it has its own mount guard.
          return applied;
        }
        // Counts come back inside the rebase response, and the reducer stores
        // them, so the operation that hit REBASE_REQUIRED is not replayed.
        dispatch({ type: 'rebase/applied', result: applied });
        dispatch({
          type: 'counts/loaded',
          counts: applied.serverCounts,
          serverDecisionKeys: Object.keys(applied.decisions) as CellKey[],
        });
        // The mirror is advanced through the same reducer rather than by
        // hand. The commit above is not visible on `stateRef` until the next
        // render, and the next caller of `rebase` - a submit's second pass -
        // builds its request from the mirror, which must not still hold the
        // pre-rebase ids or decisions.
        stateRef.current = resolutionReducer(stateRef.current, {
          type: 'rebase/applied',
          result: applied,
        });
        // Only a rebase that reopened something opens the "New conflicts"
        // dialog; a clean one (a submit's, say) leaves nothing to show.
        setNewConflicts(
          applied.reopened.length > 0 ? applied.reopened.length : null
        );
        setError(undefined);
        return applied;
      } catch (err) {
        // A rebase that itself asks to be rebased is surfaced rather than
        // looped. Every failure, an abort included, is rethrown so every
        // caller joined to this promise settles with the same error; an abort
        // just never becomes a banner.
        const apiError = toMergeApiError(err);
        const structural = detectStructuralBlock(apiError, false);
        if (structural.blocked) {
          // Terminal, not a banner; still rethrown so joined callers settle.
          if (mountedRef.current) {
            dispatch({
              type: 'structural/set',
              blocked: true,
              message: structural.message,
            });
          }
        } else if (apiError.code !== 'ABORTED' && mountedRef.current) {
          setError(apiError);
        }
        throw apiError;
      }
    })();
    rebaseRef.current = run;
    // The `.catch` marks the shared promise handled, so a caller holding it
    // without awaiting cannot raise an unhandled rejection (which crashes the
    // Jest worker); `return run` still delivers the rejection to anyone who
    // awaits. The cleanup is a side effect and `run` itself is returned, so
    // every caller - first or joined - gets the identical promise. Returning
    // `run.finally(...)` would hand out a derived promise that rejects
    // independently and is itself unhandled.
    void run
      .catch(() => undefined)
      .finally(() => {
        rebaseRef.current = null;
        if (abortRef.current.rebase === controller) {
          abortRef.current.rebase = null;
        }
        if (mountedRef.current) {
          setRebasing(false);
        }
      });
    return run;
  }, [ds, ids, requestBase]);

  // Single error path. REBASE_REQUIRED recovers by rebasing;
  // CURRENT_VERSION_UNAVAILABLE recovers by one delayed replay of the same
  // operation; anything else is surfaced and nothing optimistic is kept.
  const handleError = useCallback(
    (op: MergeOp, err: unknown, replay: () => void): void => {
      const apiError = toMergeApiError(err);
      if (apiError.code === 'ABORTED') {
        // Superseded or unmounted. Not a failure, so it must not surface, must
        // not trigger a rebase, and must not spend this operation's single
        // CURRENT_VERSION_UNAVAILABLE retry.
        return;
      }
      const structural = detectStructuralBlock(apiError, false);
      if (structural.blocked) {
        dispatch({
          type: 'structural/set',
          blocked: true,
          message: structural.message,
        });
        return;
      }
      if (apiError.code === 'REBASE_REQUIRED') {
        // `rebase` surfaces its own failure, so this caller has nothing left
        // to do with the rejection but swallow it.
        void rebase().catch(() => undefined);
        return;
      }
      if (
        apiError.code === 'CURRENT_VERSION_UNAVAILABLE' &&
        !retriedRef.current[op]
      ) {
        retriedRef.current[op] = true;
        later(replay, CURRENT_VERSION_RETRY_MS);
        return;
      }
      setError(apiError);
    },
    [later, rebase]
  );

  const fetchRows = useCallback(async (): Promise<void> => {
    const seq = ++seqRef.current.rows;
    // Install, then abort what was displaced. Doing it in this order means
    // the slot always holds the newest controller, so the abort below can
    // never reach this request's own controller however the rejection it
    // triggers is scheduled.
    const superseded = abortRef.current.rows;
    const controller = new AbortController();
    abortRef.current.rows = controller;
    superseded?.abort();
    let aborted = false;
    const s = stateRef.current;
    const section = s.ui.section;
    const req: MergeRowsRequest = {
      ...requestBase(),
      CurrentSubmissionID: s.session.currentSubmissionId,
      Decisions: sendableDecisions(s.decisions, s.noDecisionNeeded),
      Page: s.ui.requestedPages[section] ?? 1,
      PageSize: PAGE_SIZE,
      Section: section,
      Sort: ROWS_SORT,
      // Only sent after a rebase; it is what makes the server return Outcome
      // and NoDecisionNeeded.
      ...rebaseMarkers(s),
    };
    const filter = rowsFilter(s);
    if (filter) {
      req.Filter = filter;
    }
    setLoadingRows(true);
    try {
      const dto = await ds.rows(ids, req, controller.signal);
      if (!mountedRef.current || seq !== seqRef.current.rows) {
        return;
      }
      retriedRef.current.rows = false;
      // Wire shape is PascalCase; LoadedPage is lowercase.
      dispatch({
        type: 'page/loaded',
        section,
        page: { page: dto.Page, totalRows: dto.TotalRows, rows: dto.Rows },
        periodSpans: dto.PeriodSpans,
      });
      loadedRef.current[section] = loadedMark(s, section);
      // Decisions and rebases can shrink a section under the requested page;
      // step back to its last page (the rows effect then refetches it).
      const lastPage = Math.max(1, Math.ceil(dto.TotalRows / PAGE_SIZE));
      if (req.Page > lastPage) {
        dispatch({ type: 'ui/page', section, page: lastPage });
      }
      setError(undefined);
    } catch (err) {
      if (isAbort(err)) {
        // Expected: superseded by a newer rows request, or unmounted.
        aborted = true;
        return;
      }
      if (!mountedRef.current || seq !== seqRef.current.rows) {
        return;
      }
      handleError('rows', err, () => void fetchRows());
    } finally {
      if (!aborted && mountedRef.current && seq === seqRef.current.rows) {
        setLoadingRows(false);
      }
      if (abortRef.current.rows === controller) {
        abortRef.current.rows = null;
      }
    }
  }, [ds, handleError, ids, requestBase, rowsFilter]);

  // Bootstrap: diff, then (via the effect below) rows.
  useEffect(() => {
    const seq = ++seqRef.current.diff;
    let cancelled = false;
    // Effect-scoped rather than in `abortRef`: a re-run of this effect and an
    // unmount both go through the cleanup below, so there is no slot a
    // replacement could race.
    const controller = new AbortController();

    const boot = async () => {
      setLoadingDiff(true);
      try {
        const diff = await ds.diff(
          ids,
          {
            GroupRowIdentifierID: session.groupRowIdentifierId,
            MineSource: 'staging',
            SubmissionID: session.submissionId,
          },
          controller.signal
        );
        if (cancelled || !mountedRef.current || seq !== seqRef.current.diff) {
          return;
        }
        retriedRef.current.diff = false;
        dispatch({ type: 'structural/set', blocked: false, message: '' });
        dispatch({
          type: 'session/loaded',
          baseSubmissionId: diff.BaseSubmissionID,
          currentSubmissionId: diff.CurrentSubmissionID,
        });
        dispatch({
          type: 'counts/loaded',
          counts: diff.Counts,
          serverDecisionKeys: [],
        });
        // Nothing to decide but changes were auto-resolved: land on those, so
        // the user is not shown an empty "Conflicts to resolve" first. Set
        // before `setBooted`, so the first rows request is for this section.
        if (diff.Counts.TotalConflicts === 0 && diff.Counts.AutoResolved > 0) {
          dispatch({ type: 'ui/section', section: 'auto_resolved' });
        }
        setLoadingDiff(false);
        setError(undefined);
        // No merge/counts call here: with no decisions yet, diff's Counts are
        // the counts, and every merge route recomputes the whole diff.
        setBooted(true);
      } catch (err) {
        if (isAbort(err)) {
          // Expected: this effect was superseded or the hook unmounted.
          // `loadingDiff` is left alone - the boot that replaces this one has
          // already set it true synchronously, so there is no gap to close.
          return;
        }
        if (cancelled || !mountedRef.current || seq !== seqRef.current.diff) {
          return;
        }
        setLoadingDiff(false);
        const apiError = toMergeApiError(err);
        const structural = detectStructuralBlock(apiError);
        dispatch({
          type: 'structural/set',
          blocked: structural.blocked,
          message: structural.message,
        });
        if (structural.blocked) {
          // A structural block is a terminal, non-resolvable state. It is not
          // an error banner and it must not lead to a rows fetch.
          return;
        }
        handleError('diff', err, () => setRetryTick((t) => t + 1));
      }
    };

    void boot();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ds,
    ids.instanceId,
    ids.scenarioId,
    session.groupRowIdentifierId,
    session.submissionId,
    retryTick,
  ]);

  // Rows: the first run is the bootstrap's third step, and every later run is
  // a UI change (section, input, changed-underneath, page) or a rebase having
  // moved the current submission.
  useEffect(() => {
    if (!booted || state.structural.blocked) {
      return;
    }
    // Flipping back to a section already loaded for this exact view needs no
    // request. Other callers of `fetchRows` (retries, step-backs) always fetch.
    const s = stateRef.current;
    if (
      s.pages[s.ui.section] !== undefined &&
      isCurrent(loadedRef.current[s.ui.section], loadedMark(s, s.ui.section))
    ) {
      return;
    }
    void fetchRows();
    // Decisions are deliberately absent: a decision is local, and the rows
    // request carries them whenever one of the dependencies below changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    booted,
    fetchRows,
    state.structural.blocked,
    state.ui.section,
    state.ui.inputKey,
    state.ui.changedUnderneathOnly,
    state.session.currentSubmissionId,
    state.ui.requestedPages[state.ui.section],
  ]);

  // Rows of a named section's loaded page. Deliberately parameterised rather
  // than reading `ui.section`: both sections' grids are mounted at once, so an
  // action has to resolve its rows from the section whose control was used.
  const sectionRows = useCallback(
    (section: MergeSection): MergeRowDto[] =>
      stateRef.current.pages[section]?.rows ?? [],
    []
  );

  const takeSelection = useCallback(
    (rowIds: string[], section: MergeSection, choice: 'MINE' | 'THEIRS') => {
      const decisions = expandSelection(
        sectionRows(section),
        rowIds,
        section,
        choice
      );
      if (decisions.length === 0) {
        return;
      }
      dispatch({ type: 'decision/setMany', decisions });
    },
    [sectionRows]
  );

  const takeRow = useCallback(
    (rowId: string, section: MergeSection, choice: 'MINE' | 'THEIRS') => {
      takeSelection([rowId], section, choice);
    },
    [takeSelection]
  );

  // An empty list is a no-op rather than a dispatch.
  const applyDecisions = useCallback((decisions: MergeDecisionDto[]) => {
    if (decisions.length === 0) {
      return;
    }
    dispatch({ type: 'decision/setMany', decisions });
  }, []);

  const undoRow = useCallback(
    (rowId: string, section: MergeSection) => {
      const row = sectionRows(section).find((r) => r.Input_RowID === rowId);
      if (!row) {
        return;
      }
      const keys = rowCellKeys(row, section);
      if (keys.length === 0) {
        return;
      }
      dispatch({ type: 'decision/removeMany', keys });
    },
    [sectionRows]
  );

  const custom = useCallback(
    (row: MergeRowDto, period: MergePeriodDto, raw: string) => {
      const validated = validateCustomValue(raw);
      if (!validated.ok) {
        return { ok: false as const, reason: validated.reason };
      }
      const decision = customDecision(row, period, validated.value);
      if (!decision) {
        return { ok: false as const, reason: ILLEGAL_CHOICE_REASON };
      }
      dispatch({ type: 'decision/set', decision });
      return { ok: true as const };
    },
    []
  );

  const revert = useCallback((row: MergeRowDto, period: MergePeriodDto) => {
    dispatch({ type: 'decision/remove', key: periodKey(row, period) });
  }, []);

  const clear = useCallback((row: MergeRowDto, period: MergePeriodDto) => {
    const decision = clearDecision(row, period);
    if (!decision) {
      return false;
    }
    dispatch({ type: 'decision/set', decision });
    return true;
  }, []);

  const selectSection = useCallback((section: MergeSection) => {
    dispatch({ type: 'ui/section', section });
  }, []);

  const selectInput = useCallback((inputKey: string | null) => {
    dispatch({ type: 'ui/input', inputKey });
  }, []);

  const expand = useCallback((rowId: string | null) => {
    dispatch({ type: 'ui/expand', rowId });
  }, []);

  const toggleChangedUnderneath = useCallback((value: boolean) => {
    dispatch({ type: 'ui/changedUnderneathOnly', value });
  }, []);

  const setPeriodRange = useCallback((mode: PeriodRangeMode) => {
    dispatch({ type: 'ui/periodRange', mode });
  }, []);

  const setPage = useCallback((section: MergeSection, page: number) => {
    dispatch({ type: 'ui/page', section, page });
  }, []);

  const retry = useCallback(() => {
    retriedRef.current = { diff: false, rows: false };
    setError(undefined);
    setBooted(false);
    setRetryTick((t) => t + 1);
  }, []);

  // Fail closed. The Submit gate's counts start from the server counts, so
  // while an error is unrecovered they are withheld and the gate reads
  // `undefined` ("Remaining conflicts: unknown") instead of a stale figure
  // that could look resolvable. The reducer keeps the real value for retry.
  const exposed: ResolutionState = error
    ? { ...state, serverCounts: undefined }
    : state;

  return {
    state: exposed,
    dispatch,
    actions: {
      takeSelection,
      takeRow,
      applyDecisions,
      undoRow,
      custom,
      clear,
      revert,
      selectSection,
      selectInput,
      expand,
      toggleChangedUnderneath,
      setPeriodRange,
      setPage,
      rebase,
      retry,
    },
    loading: { diff: loadingDiff, rows: loadingRows },
    rebasing,
    newConflicts,
    error,
  };
}

export default useMergeSession;
